import { useState, useMemo } from 'react'
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import { useTheme } from '../contexts/ThemeContext'

interface AgeWeeklyDataPoint {
  week_number: number
  week_start: string
  week_end: string
  all_count: number
  avg_age_all: number | null
  leasing_count: number
  avg_age_leasing: number | null
  loan_count: number
  avg_age_loan: number | null
}

interface Props {
  data: AgeWeeklyDataPoint[]
  prevYearData: AgeWeeklyDataPoint[]
  year: number
}

type ChartMode = 'ukentlig' | 'aarssnitt'

// Same color per category — solid = current year, dashed = previous year
const COLORS = {
  all: '#0ea5e9',      // sky blue
  leasing: '#8b5cf6',  // purple
  loan: '#06b6d4',     // cyan/teal
}

const fmtAge = (n: number) =>
  i18n.t('components:ageWeeklyChart.ageYears', { n: n.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) })

const fmtDate = (d: string) => {
  const parts = d.split('-')
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : d
}

export default function AgeWeeklyChart({ data, prevYearData, year }: Props) {
  const { t } = useTranslation('components')
  const { isDark } = useTheme()
  const [mode, setMode] = useState<ChartMode>('ukentlig')
  const [showPrevYear, setShowPrevYear] = useState(false)
  const prevYear = year - 1

  const prevByWeek = useMemo(() => {
    const map: Record<number, AgeWeeklyDataPoint> = {}
    for (const d of prevYearData) map[d.week_number] = d
    return map
  }, [prevYearData])

  // Build prev year YTD averages
  const prevYtd = useMemo(() => {
    const sorted = [...prevYearData].sort((a, b) => a.week_number - b.week_number)
    const result: Record<number, { all: number | null; leasing: number | null; loan: number | null }> = {}
    let cumAllSum = 0, cumAllCount = 0
    let cumLeasingSum = 0, cumLeasingCount = 0
    let cumLoanSum = 0, cumLoanCount = 0
    for (const d of sorted) {
      if (d.avg_age_all != null) { cumAllSum += d.avg_age_all * d.all_count; cumAllCount += d.all_count }
      if (d.avg_age_leasing != null) { cumLeasingSum += d.avg_age_leasing * d.leasing_count; cumLeasingCount += d.leasing_count }
      if (d.avg_age_loan != null) { cumLoanSum += d.avg_age_loan * d.loan_count; cumLoanCount += d.loan_count }
      result[d.week_number] = {
        all: cumAllCount > 0 ? Math.round((cumAllSum / cumAllCount) * 10) / 10 : null,
        leasing: cumLeasingCount > 0 ? Math.round((cumLeasingSum / cumLeasingCount) * 10) / 10 : null,
        loan: cumLoanCount > 0 ? Math.round((cumLoanSum / cumLoanCount) * 10) / 10 : null,
      }
    }
    return result
  }, [prevYearData])

  const chartData = useMemo(() => {
    const weekNumbers = data.map(d => d.week_number).sort((a, b) => a - b)
    const currentByWeek: Record<number, AgeWeeklyDataPoint> = {}
    for (const d of data) currentByWeek[d.week_number] = d

    let cumAllSum = 0, cumAllCount = 0
    let cumLeasingSum = 0, cumLeasingCount = 0
    let cumLoanSum = 0, cumLoanCount = 0

    return weekNumbers.map(wn => {
      const cur = currentByWeek[wn]
      const prev = prevByWeek[wn]
      const prevYtdWeek = prevYtd[wn]

      if (cur?.avg_age_all != null) { cumAllSum += cur.avg_age_all * cur.all_count; cumAllCount += cur.all_count }
      if (cur?.avg_age_leasing != null) { cumLeasingSum += cur.avg_age_leasing * cur.leasing_count; cumLeasingCount += cur.leasing_count }
      if (cur?.avg_age_loan != null) { cumLoanSum += cur.avg_age_loan * cur.loan_count; cumLoanCount += cur.loan_count }

      return {
        week_number: wn,
        label: t('charts.weekLabel', { n: wn }),
        week_start: cur?.week_start || '',
        week_end: cur?.week_end || '',
        // Current year weekly
        avg_age_all: cur?.avg_age_all ?? null,
        avg_age_leasing: cur?.avg_age_leasing ?? null,
        avg_age_loan: cur?.avg_age_loan ?? null,
        // Previous year weekly
        prev_avg_age_all: prev?.avg_age_all ?? null,
        prev_avg_age_leasing: prev?.avg_age_leasing ?? null,
        prev_avg_age_loan: prev?.avg_age_loan ?? null,
        // Current year YTD
        ytd_avg_all: cumAllCount > 0 ? Math.round((cumAllSum / cumAllCount) * 10) / 10 : null,
        ytd_avg_leasing: cumLeasingCount > 0 ? Math.round((cumLeasingSum / cumLeasingCount) * 10) / 10 : null,
        ytd_avg_loan: cumLoanCount > 0 ? Math.round((cumLoanSum / cumLoanCount) * 10) / 10 : null,
        // Previous year YTD
        prev_ytd_avg_all: prevYtdWeek?.all ?? null,
        prev_ytd_avg_leasing: prevYtdWeek?.leasing ?? null,
        prev_ytd_avg_loan: prevYtdWeek?.loan ?? null,
      }
    })
  }, [data, prevByWeek, prevYtd, t])

  const axisColor = isDark ? '#94a3b8' : '#64748b'
  const gridColor = isDark ? '#334155' : '#e2e8f0'

  const isUkentlig = mode === 'ukentlig'
  const curAllKey = isUkentlig ? 'avg_age_all' : 'ytd_avg_all'
  const curLeasingKey = isUkentlig ? 'avg_age_leasing' : 'ytd_avg_leasing'
  const curLoanKey = isUkentlig ? 'avg_age_loan' : 'ytd_avg_loan'
  const prevAllKey = isUkentlig ? 'prev_avg_age_all' : 'prev_ytd_avg_all'
  const prevLeasingKey = isUkentlig ? 'prev_avg_age_leasing' : 'prev_ytd_avg_leasing'
  const prevLoanKey = isUkentlig ? 'prev_avg_age_loan' : 'prev_ytd_avg_loan'

  const modeLabel = isUkentlig ? t('ageWeeklyChart.modeWeeklyAvg') : t('ageWeeklyChart.modeYtdAvg')

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null
    const raw = payload[0]?.payload as any

    const curAll = raw[curAllKey]
    const curLeasing = raw[curLeasingKey]
    const curLoan = raw[curLoanKey]
    const pAll = raw[prevAllKey]
    const pLeasing = raw[prevLeasingKey]
    const pLoan = raw[prevLoanKey]

    return (
      <div className="rounded-lg shadow-lg border p-3 text-xs bg-white dark:bg-dark-surface dark:border-dark-border max-w-xs">
        <p className="font-semibold mb-1.5 text-gray-900 dark:text-gray-100">
          {label} ({fmtDate(raw.week_start)} – {fmtDate(raw.week_end)})
        </p>
        <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-1">{modeLabel}</p>
        <div className="space-y-1">
          <p className="font-medium text-gray-700 dark:text-gray-300">{year}:</p>
          <div className="flex justify-between gap-4">
            <span style={{ color: COLORS.all }}>● {t('ageWeeklyChart.all')}</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{curAll != null ? fmtAge(curAll) : '–'}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span style={{ color: COLORS.leasing }}>● {t('ageWeeklyChart.leasing')}</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{curLeasing != null ? fmtAge(curLeasing) : '–'}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span style={{ color: COLORS.loan }}>● {t('ageWeeklyChart.loan')}</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{curLoan != null ? fmtAge(curLoan) : '–'}</span>
          </div>

          {showPrevYear && (
          <>
            <p className="font-medium text-gray-700 dark:text-gray-300 mt-2">{t('ageWeeklyChart.prevYearDashed', { year: prevYear })}</p>
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.all }}>┅ {t('ageWeeklyChart.all')}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{pAll != null ? fmtAge(pAll) : '–'}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.leasing }}>┅ {t('ageWeeklyChart.leasing')}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{pLeasing != null ? fmtAge(pLeasing) : '–'}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.loan }}>┅ {t('ageWeeklyChart.loan')}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{pLoan != null ? fmtAge(pLoan) : '–'}</span>
            </div>
          </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          {t('ageWeeklyChart.title', { year })}
        </h2>
        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer mr-4 px-2 py-1 rounded-lg border border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-hover transition-colors">
            <input type="checkbox" checked={showPrevYear} onChange={e => setShowPrevYear(e.target.checked)} className="rounded-sm" />
            {prevYear}
          </label>
        <div className="flex rounded-lg border border-gray-200 dark:border-dark-border overflow-hidden">
          {(['ukentlig', 'aarssnitt'] as ChartMode[]).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                mode === m
                  ? 'bg-primary-500 text-white'
                  : 'bg-white dark:bg-dark-card text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-hover'
              }`}
            >
              {m === 'ukentlig' ? t('ageWeeklyChart.weekly') : t('ageWeeklyChart.ytd')}
            </button>
          ))}
        </div>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {isUkentlig
          ? t('ageWeeklyChart.subtitleWeekly')
          : t('ageWeeklyChart.subtitleYtd')}
      </p>

      <ResponsiveContainer width="100%" height={420}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            dataKey="label"
            tick={{ fill: axisColor, fontSize: 11 }}
            tickLine={{ stroke: axisColor }}
            axisLine={{ stroke: axisColor }}
            interval={Math.max(0, Math.floor(chartData.length / 13) - 1)}
          />
          <YAxis
            domain={['auto', 'auto']}
            tickFormatter={(v: number) => t('ageWeeklyChart.ageYears', { n: v.toFixed(0) })}
            tick={{ fill: axisColor, fontSize: 11 }}
            tickLine={{ stroke: axisColor }}
            axisLine={{ stroke: axisColor }}
            label={{ value: t('ageWeeklyChart.ageAxis'), angle: -90, position: 'insideLeft', style: { fill: axisColor, fontSize: 12 } }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12, color: axisColor }} iconType="circle" />

          {/* Current year — solid lines */}
          <Line type="monotone" dataKey={curAllKey} name={t('ageWeeklyChart.allYear', { year })} stroke={COLORS.all} strokeWidth={2} dot={isUkentlig ? { r: 2 } : false} activeDot={{ r: 5 }} connectNulls />
          <Line type="monotone" dataKey={curLeasingKey} name={t('ageWeeklyChart.leasingYear', { year })} stroke={COLORS.leasing} strokeWidth={2} dot={isUkentlig ? { r: 2 } : false} activeDot={{ r: 5 }} connectNulls />
          <Line type="monotone" dataKey={curLoanKey} name={t('ageWeeklyChart.loanYear', { year })} stroke={COLORS.loan} strokeWidth={2} dot={isUkentlig ? { r: 2 } : false} activeDot={{ r: 5 }} connectNulls />

          {/* Previous year — dashed lines, same colors */}
          {showPrevYear && (
          <>
            <Line type="monotone" dataKey={prevAllKey} name={t('ageWeeklyChart.allYear', { year: prevYear })} stroke={COLORS.all} strokeWidth={2} strokeDasharray="6 3" dot={false} activeDot={{ r: 4 }} connectNulls={false} />
            <Line type="monotone" dataKey={prevLeasingKey} name={t('ageWeeklyChart.leasingYear', { year: prevYear })} stroke={COLORS.leasing} strokeWidth={2} strokeDasharray="6 3" dot={false} activeDot={{ r: 4 }} connectNulls={false} />
            <Line type="monotone" dataKey={prevLoanKey} name={t('ageWeeklyChart.loanYear', { year: prevYear })} stroke={COLORS.loan} strokeWidth={2} strokeDasharray="6 3" dot={false} activeDot={{ r: 4 }} connectNulls={false} />
          </>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}
